import { Component, QueryList, HostListener, OnInit, OnDestroy, input, inject } from '@angular/core';
import { FocusKeyManager } from '@angular/cdk/a11y';
import { Subscription } from 'rxjs';

import { ApFocusKeyManagerFactory } from '@ap/cdk';

import { ApAccordionItemComponent } from './item/item.component';
import { ApAccordionService } from './accordion.service';

/**
 * @deprecated in favor of [`@asn/accordion`](https://designsystem.acc.aws.devolksbank.net/index.html?path=/docs/web-components-accordion--docs). To be removed with the Angular 23 migration.
 */
@Component({
    selector: 'ap-accordion',
    templateUrl: './accordion.component.html',
    styleUrls: ['./accordion.component.scss'],
    providers: [ApFocusKeyManagerFactory, ApAccordionService]
})
export class ApAccordionComponent implements OnInit, OnDestroy {
    private readonly focusKeyManagerFactory = inject(ApFocusKeyManagerFactory);
    private readonly accordionService = inject(ApAccordionService);

    /**
     * This will leave one accordion item open at the time.
     * Does not update dynamically. Only set on init
     */
    public readonly singleItemOpen = input<boolean>(false);

    /**
     * Give items a size. Possible options are `medium` and `large`
     * Does not update dynamically. Only set on init
     * Default is `large
     */
    public readonly size = input<'medium' | 'large'>('large');

    private keyManager: FocusKeyManager<ApAccordionItemComponent>;
    private subscription: Subscription;

    @HostListener('window:keydown', ['$event'])
    protected handleKeyDown(event: KeyboardEvent) {
        if (event.code === 'ArrowUp') {
            this.keyManager.setPreviousItemActive();
            event.preventDefault();
        } else if (event.code === 'ArrowDown') {
            this.keyManager.setNextItemActive();
            event.preventDefault();
        }
    }

    public ngOnInit(): void {
        this.subscription = this.accordionService.itemRegistered$.subscribe((accordionItem) => {
            const queryList = new QueryList<ApAccordionItemComponent>();
            queryList.reset(this.accordionService.accordionItems);

            this.keyManager = this.focusKeyManagerFactory.create(queryList).withWrap();

            // Disgusting hack to prevent ExpressionChanged error when adding children dynamically (*ngFor / Dynamic Content)
            // These dynamic items are embedded views and embedded views are rendered before their parent, therefore an error will be thrown
            setTimeout(() => {
                accordionItem.variant.set(this.getChildrenVariant());
            }, 0);

            accordionItem.onClick.subscribe(() => {
                this.keyManager.setActiveItem(this.accordionService.accordionItems.indexOf(accordionItem));
            });

            if (this.singleItemOpen()) {
                accordionItem.onOpen.subscribe(() => {
                    this.accordionService.accordionItems.forEach((item, ind) => {
                        item.isOpen.set(ind === this.accordionService.accordionItems.indexOf(accordionItem));
                    });
                });
            }
        });
    }

    public ngOnDestroy(): void {
        this.subscription?.unsubscribe();
    }

    private getChildrenVariant(): string {
        return this.size();
    }
}
